import { useAuth } from "../context/AuthContext";
import { PARTNER_ROLE_KEY } from "../constants/roles";

/**
 * Returns a `can(permission)` helper based on the current user's permissions
 * from /me. Also exposes `role` for coarse-grained checks, and `isTechnician`
 * for the field-side/office-side question.
 *
 * Usage:
 *   const { can } = usePermissions();
 *   if (can("jobs.write")) { ... }
 */
export function usePermissions() {
  const { me } = useAuth();
  const permissions = me?.permissions ?? [];

  return {
    can: (permission) => permissions.includes(permission),
    canAny: (...perms) => perms.some((p) => permissions.includes(p)),
    permissions,
    role: me?.role ?? null,
    // ⚠️⚠️ THE field-side check. Use this, never `role === "technician"`.
    //
    // The role NAME is a denylist and fails OPEN: a custom field-side role is
    // called something else, so it sailed through every `!== "technician"`
    // gate and picked up visit Edit/Delete, PO write buttons and the PMO-only
    // checklist scopes. The backend converted its four equivalents to the
    // tier-aware `is_technician` when custom roles landed (auth/roles.py); this
    // is the frontend's counterpart, and `_serialize_user` ships `base_tier`
    // on every user precisely so this can read it.
    //
    // ⚠️ The `role` fallback is deliberate, not redundant. /me is cached in
    // sessionStorage, so a session predating `base_tier` carries none — and a
    // bare `undefined === "technician"` is false, which would hand a real
    // technician the edit buttons. Falling back to the literal fails closed.
    //
    // A question with a real permission behind it (may they see pricing?)
    // should use `can(...)` instead — this is only for "office or field".
    isTechnician: me?.base_tier
      ? me.base_tier === "technician"
      : me?.role === "technician",
    // Partners never clock in or out (see constants/roles.js).
    isPartner: me?.role === PARTNER_ROLE_KEY,
    // The office-side counterpart. There are only two tiers (see CLAUDE.md's
    // "Two tiers only, by design"), so this is exactly !isTechnician — a named
    // export rather than making every call site spell out the inverse, and it
    // reads the same way the backend's `is_management` does.
    isManagement: me?.base_tier
      ? me.base_tier === "management"
      : Boolean(me?.role) && me.role !== "technician",
  };
}
